import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    ActivitySessionsActivitySelect,
    ActivitySessionsCourseSelect,
    ActivitySessionsTotalCounts,
    type ActivityKind,
} from "@khan/activity-sessions-query";
import {
    PageloadMarker,
    type TrackedPageSlug,
} from "@khan/analytics-performance";
import {
    DateRangeDropdown,
    getSelectedDateRange,
    type DateRange,
    type DateRangeSelection,
    type DateRangeValue,
} from "@khan/component-date-range-dropdown";
import {EmptyState} from "@khan/component-simple-table";
import {CookieCategory} from "@khan/data-cookies";
import {mediaQueries} from "@khan/shared-styles";
import {LocalStore} from "@khan/util-local-store";

import PaginationPrevNextButtons from "#coach-dashboard-package/shared-components/pagination-prev-next-buttons.tsx";

import FiltersContainer from "../filters-container.tsx";
import ReportLayout from "../report-layout.tsx";

import ActivitySessionsReportQuery, {
    type ActivitySessionsQueryResult,
} from "./activity-sessions-report-query.tsx";
import ActivitySessionsTable from "./activity-sessions-table.tsx";
import type {CourseType} from "./types.ts";

export const ACTIVITY_SESSIONS_DATE_RANGE_STORAGE_KEY =
    "districts-classroom-package-activity-sessions-date-range-storage-key";
export const DEFAULT_LEARNER_DATE_RANGE_VALUE: DateRangeValue = "this_week";
export const DEFAULT_DATE_RANGE_VALUE: DateRangeValue = "last-7-days";
export const LIHP_INDV_STUDENT_REPORT: TrackedPageSlug =
    "lihp_individual_student_report";
export const TEACHER_DASH_INDV_STUDENT_REPORT: TrackedPageSlug =
    "ccl_individual_student_report";

const BACKFILL_DATE: moment.Moment = moment().subtract(2, "years");

type Props = {
    studentKaid: string;
    dateRangeSelection: DateRangeSelection | null | undefined;
    selectedCourseFilter: CourseType | null | undefined;
    isNweaMapSynced: boolean;
    emptyState?: React.ReactNode;
    pageSource: TrackedPageSlug;
};

type State = {
    selectedCourseFilter: CourseType | null | undefined;
    selectedActivityFilter: ActivityKind | null | undefined;
    dateRangeSelection: DateRangeSelection;
};

class ActivitySessionsReport extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        const {dateRangeSelection, selectedCourseFilter = null} = props;

        const startingDataRangeSelection =
            dateRangeSelection || getDefaultDateRange();

        this.state = {
            selectedCourseFilter,
            selectedActivityFilter: null,
            dateRangeSelection: {...startingDataRangeSelection},
        };
    }

    /**
     * Returns a key that changes when the filters change. This allows us to
     * reset the query components when the filters change.
     */
    getPaginationKey: () => string = () => {
        const {studentKaid} = this.props;
        const {
            selectedCourseFilter,
            selectedActivityFilter,
            dateRangeSelection,
        } = this.state;

        return `${studentKaid}#${selectedCourseFilter || ""}#${
            selectedActivityFilter || ""
        }#${dateRangeSelection.selectedValue}`;
    };

    renderTable: (arg1: ActivitySessionsQueryResult) => React.ReactNode = ({
        activityTime,
        sessions,
        fetchPage,
        nextCursor,
        isLoading,
    }: ActivitySessionsQueryResult) => {
        const {emptyState = <EmptyState />} = this.props;
        const {pageSource, studentKaid} = this.props;
        return (
            <React.Fragment>
                {/** Add shimmer or overlay on activity session to
                 * make the user experience less jarring  when loading
                 * pages
                 */}
                <View style={styles.content} testId="activity-log-content">
                    {isLoading && !sessions.length ? (
                        <CircularSpinner style={styles.spinner} size="medium" />
                    ) : sessions.length ? (
                        <ActivitySessionsTable
                            data={sessions}
                            studentKaid={studentKaid}
                        />
                    ) : (
                        emptyState
                    )}
                    {!!sessions.length && (
                        <View style={styles.paginationContainer}>
                            <PaginationPrevNextButtons
                                fetchPage={fetchPage}
                                nextCursor={nextCursor}
                            />
                            {isLoading && <CircularSpinner size="small" />}
                        </View>
                    )}
                </View>
                <PageloadMarker
                    pageName={pageSource}
                    sufficientlyUsable={!isLoading}
                    fullyInteractive={!isLoading}
                />
            </React.Fragment>
        );
    };

    handleDateRangeChange: (dateRangeSelection: DateRangeSelection) => void = (
        dateRangeSelection: DateRangeSelection,
    ) => {
        this.setState({
            dateRangeSelection: {...dateRangeSelection},
        });
    };

    handleCourseFilterChange: (
        selectedCourseFilter?: CourseType | null | undefined,
    ) => void = (selectedCourseFilter?: CourseType | null) => {
        this.setState({selectedCourseFilter});

        if (selectedCourseFilter) {
            this.handleActivityFilterChange(null);
        }
    };

    handleActivityFilterChange: (
        selectedActivityFilter?: ActivityKind | null | undefined,
    ) => void = (selectedActivityFilter?: ActivityKind | null) =>
        this.setState({selectedActivityFilter});

    renderFilters: (result: ActivitySessionsQueryResult) => React.ReactNode = (
        result: ActivitySessionsQueryResult,
    ) => {
        const {
            selectedCourseFilter,
            selectedActivityFilter,
            dateRangeSelection,
        } = this.state;

        const {isNweaMapSynced} = this.props;
        const {activityTime, isLoading} = result;

        const showSessionTotalCounts =
            !isLoading && !selectedCourseFilter && !selectedActivityFilter;

        const saveSelectionToLocalStore = (
            selectedValue: DateRangeValue,
            dateRange?: DateRange | null,
        ) => {
            LocalStore.set(
                ACTIVITY_SESSIONS_DATE_RANGE_STORAGE_KEY,
                {
                    selectedValue,
                    dateRange,
                },
                CookieCategory.Functional,
            );
        };

        return (
            <View style={styles.filtersContainer}>
                <FiltersContainer>
                    <DateRangeDropdown
                        onChange={this.handleDateRangeChange}
                        onChangeCallback={saveSelectionToLocalStore}
                        dateRangeSelection={dateRangeSelection}
                        options={[
                            {label: t`Today`, value: "today"},
                            {label: t`This week`, value: "this_week"},
                            {label: t`Last 30 days`, value: "last-30-days"},
                        ]}
                    />
                    <ActivitySessionsCourseSelect
                        selectedCourseFilter={selectedCourseFilter}
                        handleCourseFilterChange={this.handleCourseFilterChange}
                        includeKmap={isNweaMapSynced}
                    />
                    {!selectedCourseFilter && (
                        <ActivitySessionsActivitySelect
                            selectedActivityFilter={selectedActivityFilter}
                            handleActivityFilterChange={
                                this.handleActivityFilterChange
                            }
                        />
                    )}
                </FiltersContainer>
                {showSessionTotalCounts && (
                    <View style={styles.activityTimeContainer}>
                        <ActivitySessionsTotalCounts
                            activityTime={activityTime}
                        />
                    </View>
                )}
            </View>
        );
    };

    renderIncompleteDataAlert(): React.ReactNode {
        const formattedDate = BACKFILL_DATE.format("MMMM D, YYYY");
        const message = t`Data in this report only goes back to ${formattedDate}.`;

        // TODO(FEI-4848): This banner used to have a clock icon before being
        // updated to use Wonder Blocks Banner. If icon support is added
        // to WB Banner, update this banner here to have a clock icon again.
        return (
            <View>
                <Banner text={message} kind="info" />
                <Strut size={spacing.large_24} />
            </View>
        );
    }

    render(): React.ReactNode {
        const {studentKaid} = this.props;
        const {
            dateRangeSelection: {dateRange},
            selectedCourseFilter,
            selectedActivityFilter,
        } = this.state;

        const startDate: moment.Moment | null | undefined = dateRange
            ? moment(dateRange.startDate)
            : null;

        const showDataAlert = startDate && startDate.isBefore(BACKFILL_DATE);

        return (
            <ActivitySessionsReportQuery
                studentKaid={studentKaid}
                dateRange={dateRange}
                courseFilter={selectedCourseFilter}
                activityFilter={selectedActivityFilter}
                key={this.getPaginationKey()}
            >
                {(result: ActivitySessionsQueryResult) => (
                    <ReportLayout filters={this.renderFilters(result)}>
                        {showDataAlert && this.renderIncompleteDataAlert()}
                        {this.renderTable(result)}
                    </ReportLayout>
                )}
            </ActivitySessionsReportQuery>
        );
    }
}

function getDefaultDateRange(): DateRangeSelection {
    return getSelectedDateRange(null, DEFAULT_DATE_RANGE_VALUE);
}

const styles = StyleSheet.create({
    spinner: {
        paddingTop: spacing.xLarge_32,
        alignItems: "center",
    },
    paginationContainer: {
        marginTop: spacing.large_24,
        flexDirection: "row",
    },
    filtersContainer: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        height: 48,

        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
            height: "auto",
        },
    },
    activityTimeContainer: {
        marginLeft: spacing.medium_16,

        [mediaQueries.mdOrSmaller]: {
            marginLeft: 0,
            marginTop: spacing.medium_16,
        },
    },
    content: {
        // The activity time component pushes the table 4px down (creating more
        // distance between the filters and table). This margin accounts for
        // that so that the distance between the filters and table is standard.
        marginTop: -spacing.xxxSmall_4,
    },
});

export default ActivitySessionsReport;
